import { service } from '@ember/service'
import { TeacherTotter } from '@blakeelearning/teacher-totter'
import config from 'teacher-ui-client/config/environment'
import Service from '@ember/service'
import type { SubscriptionTypeService } from './subscription-type'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type SessionService from './session'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type FeaturesService from './features'
import type { RouteInfoWithAttributes } from '@ember/routing/-internals'

export default class TeacherEvents extends Service {
  @service authToken!: AuthToken
  @service features!: FeaturesService
  @service session!: SessionService
  @service subscriptionType!: SubscriptionTypeService
  teacherTotter?: TeacherTotter

  initialized = false

  setupTeacherTotter() {
    if (!this.features.isEnabled('teacher_totter')) return

    this.teacherTotter = new TeacherTotter({
      districtId: Number(this.session.currentSchool?.district?.id),
      application: {
        name: 'teacher-ui',
      },
      options: {
        debugMode: config.environment === 'development',
        syncOptions: {
          syncUrl: config.teacherTotter.url,
          getAuthCallback: async () => {
            if (this.authToken.isExpired(this.authToken.tokenExpiry)) {
              await this.authToken.refreshToken()
              return Promise.resolve(this.authToken.token)
            }
            return Promise.resolve(this.authToken.token)
          },
        },
      },
    })
    this.initialized = true
  }

  track(to?: RouteWithKnownAttributes) {
    if (!to) return
    if (this.initialized) {
      const product = this.subscriptionType.currentValue || SubscriptionType.Reading
      const routeName = calculateResolvedRouteName(to)
      console.log(routeName)
      void this.teacherTotter?.trackSession(routeName, product)
    }
  }

  sendEvent(eventName: string, product: string, eventData?: Record<string, any>) {
    if (this.features.isEnabled('teacher_totter') && this.initialized) {
      void this.teacherTotter?.sendEvent(eventName, product, eventData)
    }
  }
}

function calculateResolvedRouteName(to: RouteWithKnownAttributes) {
  if (to?.name?.startsWith('assignments.product')) {
    const product = to?.attributes?.product || to?.attributes?.assignmentProduct || 'product'
    return to?.name.replace('product', product)
  }

  if (to?.name?.startsWith('reporting.ui-scope.')) {
    const uiScope = lookupUIScope(to) || 'ui-scope'
    return to.name.replace('ui-scope', uiScope)
  }

  return to?.name || 'unknown'
}

export type RouteWithKnownAttributes =
  | (RouteInfoWithAttributes & {
      attributes?: { product?: string; assignmentProduct?: string; uiScope?: string }
    })
  | null

function lookupUIScope(to: RouteWithKnownAttributes) {
  let firstParent = to?.parent as RouteWithKnownAttributes
  let depth = 0
  while (firstParent && depth < 10) {
    if (!firstParent) break

    if (firstParent?.attributes?.uiScope?.scope) {
      return firstParent.attributes?.uiScope?.scope as string
    }
    firstParent = firstParent.parent as RouteWithKnownAttributes
    depth++
  }
}

declare module '@ember/service' {
  interface Registry {
    'teacher-events': TeacherEvents
  }
}
